
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth';
import { useToast } from '@/hooks/use-toast';
import { generatePrayer } from '@/utils/abiel';
import { usePrayerStore } from '@/hooks/usePrayerStore';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';
import { AI_PRAYER_ASSISTANT_SETTINGS_KEY, AI_PRAYER_ASSISTANT_SETTINGS_UPDATED } from '@/constants/storage.constants';

export interface FormData {
  requestText: string;
  subject: string;
  selectedTags: string[];
  includeBibleVerse: boolean;
  bilingual: boolean;
}

export interface FormSettings {
  availableTags: string[];
  enableSubject: boolean;
  enableScripture: boolean;
  enableBilingual: boolean;
  maxRequestLength: number;
  assistantTitle: string;
  assistantSubtitle: string;
}

const defaultFormData: FormData = {
  requestText: '',
  subject: '',
  selectedTags: [],
  includeBibleVerse: true,
  bilingual: false
};

const defaultFormSettings: FormSettings = {
  availableTags: [
    'Healing', 'Guidance', 'Protection', 'Thanksgiving', 
    'Wisdom', 'Forgiveness', 'Strength', 'Peace',
    'Family', 'Work', 'Health', 'Relationships'
  ],
  enableSubject: true,
  enableScripture: true,
  enableBilingual: true,
  maxRequestLength: 500,
  assistantTitle: 'AI Prayer Assistant',
  assistantSubtitle: 'Share your heart, and let Abiel help you pray'
};

export function useAiPrayerAssistantForm() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { addPrayer } = usePrayerStore();
  const { stop } = useAbielAudio();
  
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [formSettings, setFormSettings] = useState<FormSettings>(defaultFormSettings);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedPrayer, setGeneratedPrayer] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedPrayerId, setSavedPrayerId] = useState<string | null>(null);

  // Load form settings from localStorage
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem(AI_PRAYER_ASSISTANT_SETTINGS_KEY);
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        setFormSettings(prev => ({ ...prev, ...parsed }));
      }
    } catch (error) {
      console.error('Error loading form settings:', error);
    }

    // Listen for settings updates
    const handleSettingsUpdate = () => {
      try {
        const updatedSettings = localStorage.getItem(AI_PRAYER_ASSISTANT_SETTINGS_KEY);
        if (updatedSettings) {
          const parsed = JSON.parse(updatedSettings);
          setFormSettings(prev => ({ ...prev, ...parsed }));
        }
      } catch (error) {
        console.error('Error handling settings update:', error);
      }
    };

    window.addEventListener(AI_PRAYER_ASSISTANT_SETTINGS_UPDATED, handleSettingsUpdate);
    return () => {
      window.removeEventListener(AI_PRAYER_ASSISTANT_SETTINGS_UPDATED, handleSettingsUpdate);
    };
  }, []);

  const updateFormData = (updates: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const updateFormSettings = (updates: Partial<FormSettings>) => {
    const newSettings = { ...formSettings, ...updates };
    setFormSettings(newSettings);
    
    try {
      localStorage.setItem(AI_PRAYER_ASSISTANT_SETTINGS_KEY, JSON.stringify(newSettings));
      window.dispatchEvent(new CustomEvent(AI_PRAYER_ASSISTANT_SETTINGS_UPDATED));
    } catch (error) {
      console.error('Error saving form settings:', error);
    }
  };

  const submitRequest = async (): Promise<boolean> => {
    if (!formData.requestText.trim()) {
      toast({
        title: "Prayer request required",
        description: "Please share what you'd like prayer for.",
        variant: "destructive"
      });
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Stop any existing audio before generating new prayer
      stop();

      const result = await generatePrayer({
        message: formData.requestText,
        tags: formData.selectedTags,
        includeVerse: formData.includeBibleVerse,
        subject: formData.subject,
        bilingual: formData.bilingual
      });

      if (result.error) {
        throw new Error(result.error);
      }

      const prayer = result.prayer || 'No prayer generated';

      setGeneratedPrayer(prayer);
      
      toast({
        title: "Prayer Generated",
        description: "Your personalized prayer has been created by Abiel."
      });

      return true;
    } catch (err: any) {
      console.error('Error generating prayer:', err);
      setError(err.message);
      toast({
        title: "Error",
        description: err.message || "Failed to generate prayer. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const savePrayerToDatabase = async (visibility: 'private' | 'public' = 'private'): Promise<boolean> => {
    if (!generatedPrayer || !user) {
      toast({
        title: "Unable to save",
        description: !user ? "Please sign in to save prayers." : "No prayer to save.",
        variant: "destructive"
      });
      return false;
    }

    try {
      console.log('Starting prayer save process...', { visibility, hasUser: !!user, hasPrayer: !!generatedPrayer });

      const prayerTitle = formData.subject || 
        `Prayer for ${formData.selectedTags[0] || 'General Request'}` ||
        'AI Generated Prayer';

      console.log('Prayer data to save:', {
        title: prayerTitle,
        author: user.email || user.id,
        category: formData.selectedTags[0] || 'general',
        visibility
      });

      const savedPrayer = await addPrayer({
        id: crypto.randomUUID(),
        date: new Date().toISOString(),
        title: prayerTitle,
        content: generatedPrayer,
        author: user.email || user.id,
        category: formData.selectedTags[0] || 'general',
        visibility,
        status: 'active',
        group: null,
        isFavorite: false,
        isBookmarked: false,
      });

      console.log('Prayer save result:', savedPrayer);

      if (savedPrayer) {
        setSavedPrayerId(savedPrayer.id);
        toast({
          title: "Prayer Saved",
          description: `Your prayer has been saved ${visibility === 'public' ? 'publicly' : 'privately'}.`,
        });
        return true;
      } else {
        console.error('addPrayer returned null/false');
        toast({
          title: "Save failed",
          description: "Unable to save prayer. Please try again.",
          variant: "destructive"
        });
        return false;
      }
    } catch (err: any) {
      console.error('Error saving prayer:', err);
      toast({
        title: "Error saving prayer",
        description: err.message || "Please try again.",
        variant: "destructive"
      });
      return false;
    }
  };

  const resetForm = () => {
    setFormData(defaultFormData);
    setGeneratedPrayer(null);
    setError(null);
    setSavedPrayerId(null);
  };

  return {
    formData,
    updateFormData,
    formSettings,
    updateFormSettings,
    submitRequest,
    savePrayerToDatabase,
    isLoading,
    generatedPrayer,
    error,
    savedPrayerId,
    resetForm
  };
}
